import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { getRecordCardHeight } from '../BrandedShareChrome/BrandedListingCard';

const CARD_BG = '#242424';
const DIVIDER = 'rgba(255,255,255,0.08)';
const skPrimarySx = { bgcolor: '#383838', borderRadius: '4px' };
const skMutedSx = { bgcolor: '#2E2E2E', borderRadius: '4px' };
const skAccentSx = { bgcolor: 'rgba(109,168,167,0.24)', borderRadius: '4px' };

/** Header (ubicación · catastral + ⋮), título, monto, línea de metas y línea comercial. */
const CardBodySkeleton = ({ idx, dense, height }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, ...(height ? { height, flexShrink: 0 } : null), overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '12px', py: '5px', borderBottom: `1px solid ${DIVIDER}` }}>
      <Skeleton variant="rounded" height={12} sx={{ ...skMutedSx, flex: 1, maxWidth: `${72 - (idx % 3) * 8}%` }} />
      <Skeleton variant="circular" width={18} height={18} sx={skMutedSx} />
    </Box>
    <Box sx={{ px: '12px', pt: '8px', pb: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Skeleton variant="rounded" height={14} width={`${70 - (idx % 4) * 7}%`} sx={skPrimarySx} />
      <Box>
        <Skeleton variant="rounded" height={10} width={64} sx={skAccentSx} />
        <Skeleton variant="rounded" height={dense ? 18 : 20} width={dense ? '62%' : 120} sx={{ ...skPrimarySx, mt: '3px' }} />
      </Box>
      <Skeleton variant="rounded" height={12} width={`${64 - (idx % 3) * 9}%`} sx={skPrimarySx} />
      <Skeleton variant="rounded" height={12} width={`${48 - (idx % 2) * 10}%`} sx={skMutedSx} />
    </Box>
  </Box>
);

/**
 * Skeleton de la vista por registro: misma forma que `RecordListingCard`
 * (media angosta a la izquierda en lista, 16:9 arriba en grilla) y el mismo alto
 * fijo (`getRecordCardHeight`), así no salta al llegar las cards.
 * @param {{ count?: number, layout?: 'list'|'grid' }} props
 */
export default function LoadingRecordCards({ count = 6, layout = 'list' }) {
  const isGrid = layout === 'grid';
  const height = getRecordCardHeight(layout);
  return (
    <Box
      data-testid="loading-record-cards"
      sx={isGrid
        ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }
        : { display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <Box
          key={idx}
          data-testid="loading-record-card"
          sx={{
            borderRadius: '10px',
            bgcolor: CARD_BG,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: isGrid ? 'column' : 'row',
            ...(isGrid ? null : { height }),
          }}
        >
          <Skeleton
            variant="rectangular"
            sx={{
              bgcolor: 'rgba(255,255,255,0.04)',
              flexShrink: 0,
              ...(isGrid
                ? { width: '100%', height: 'auto', aspectRatio: '16 / 9' }
                : { width: { xs: 96, sm: 120 }, height: '100%', alignSelf: 'stretch' }),
            }}
          />
          <CardBodySkeleton idx={idx} dense={isGrid} height={isGrid ? height : null} />
        </Box>
      ))}
    </Box>
  );
}
